import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import OrgPrintChrome from './OrgPrintChrome';

function UOMManagement() {
  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedUOM, setSelectedUOM] = useState(null);
  const [filterType, setFilterType] = useState('ALL');
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    symbol: '',
    description: '',
    type: 'COUNT',
    conversionFactorToBase: '1',
    isActive: true
  });

  const uomTypes = [
    'ALL',
    'WEIGHT',
    'LENGTH',
    'VOLUME',
    'COUNT',
    'AREA',
    'TIME',
    'TEMPERATURE'
  ];

  useEffect(() => {
    fetchUOMs();
  }, []);

  const fetchUOMs = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/system-admin/units-of-measurement');
      setUoms(response.data);
    } catch (error) {
      toast.error('Error fetching units of measurement');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({
      name: '',
      symbol: '',
      description: '',
      type: 'COUNT',
      conversionFactorToBase: '1',
      isActive: true
    });
    setSelectedUOM(null);
    setShowModal(true);
  };

  const handleEdit = (uom) => {
    setFormData({
      name: uom.name,
      symbol: uom.symbol,
      description: uom.description || '',
      type: uom.type,
      conversionFactorToBase:
        uom.conversionFactorToBase != null && uom.conversionFactorToBase !== ''
          ? String(uom.conversionFactorToBase)
          : (uom.type === 'TEMPERATURE' ? '' : '1'),
      isActive: uom.isActive
    });
    setSelectedUOM(uom);
    setShowModal(true);
  };

  const handleViewPrint = (uom) => {
    setPrintData(uom);
    setShowPrintModal(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDelete = async (id, isSystem) => {
    if (isSystem) {
      toast.error('Cannot delete system UOM');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this unit of measurement?')) {
      return;
    }

    try {
      await axios.delete(`/api/system-admin/units-of-measurement/${id}`);
      toast.success('Unit of measurement deleted successfully');
      fetchUOMs();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Error deleting unit of measurement';
      toast.error(errorMsg);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const isTemperature = formData.type === 'TEMPERATURE';
      const factorRaw = (formData.conversionFactorToBase || '').trim();
      let conversionFactorToBase = null;
      if (!isTemperature) {
        if (!factorRaw) {
          toast.error('Conversion factor to base is required (except for Temperature)');
          return;
        }
        conversionFactorToBase = parseFloat(factorRaw);
        if (isNaN(conversionFactorToBase) || conversionFactorToBase <= 0) {
          toast.error('Conversion factor to base must be a number greater than zero');
          return;
        }
      }

      const payload = {
        name: formData.name,
        symbol: formData.symbol,
        description: formData.description || null,
        type: formData.type,
        conversionFactorToBase,
        isActive: formData.isActive
      };

      if (selectedUOM) {
        await axios.put(`/api/system-admin/units-of-measurement/${selectedUOM.id}`, payload);
        toast.success('Unit of measurement updated successfully');
      } else {
        await axios.post('/api/system-admin/units-of-measurement', payload);
        toast.success('Unit of measurement created successfully');
      }
      setShowModal(false);
      fetchUOMs();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Error saving unit of measurement';
      toast.error(errorMsg);
    }
  };

  const filteredUOMs = filterType === 'ALL' 
    ? uoms 
    : uoms.filter(uom => uom.type === filterType);

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="d-flex justify-content-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <p className="text-muted mb-0">
              Manage system-wide units and conversion factors for inventory (e.g. L ↔ mL)
            </p>
            <Button variant="primary" onClick={handleCreate}>
              + Create UOM
            </Button>
          </div>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Filter by Type</h5>
                <Form.Select
                  style={{ width: 'auto' }}
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  {uomTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </Form.Select>
              </div>
            </Card.Header>
            <Card.Body>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Symbol</th>
                    <th>Type</th>
                    <th>Factor to Base</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>System</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUOMs.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center text-muted">
                        No units of measurement found
                      </td>
                    </tr>
                  ) : (
                    filteredUOMs.map((uom) => (
                      <tr key={uom.id}>
                        <td><strong>{uom.name}</strong></td>
                        <td><code>{uom.symbol}</code></td>
                        <td>
                          <Badge bg="info">{uom.type}</Badge>
                        </td>
                        <td>
                          {uom.conversionFactorToBase != null
                            ? Number(uom.conversionFactorToBase).toLocaleString('en-US', {
                                maximumFractionDigits: 10
                              })
                            : <span className="text-muted">—</span>}
                        </td>
                        <td>{uom.description || '-'}</td>
                        <td>
                          {uom.isActive ? (
                            <Badge bg="success">Active</Badge>
                          ) : (
                            <Badge bg="danger">Inactive</Badge>
                          )}
                        </td>
                        <td>
                          {uom.isSystem ? (
                            <Badge bg="warning">System</Badge>
                          ) : (
                            <Badge bg="secondary">Custom</Badge>
                          )}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-info"
                            className="me-2"
                            onClick={() => handleViewPrint(uom)}
                            title="View & Print"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                              <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                              <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                            </svg>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="me-2"
                            onClick={() => handleEdit(uom)}
                            title="Edit"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                              <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                              <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                            </svg>
                          </Button>
                          {!uom.isSystem && (
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDelete(uom.id, uom.isSystem)}
                              title="Delete"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                              </svg>
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Print View Modal */}
      <Modal show={showPrintModal} onHide={() => setShowPrintModal(false)} size="lg" className="print-modal">
        <Modal.Header className="print-header">
          <Modal.Title>Unit of Measurement</Modal.Title>
          <div className="print-actions">
            <Button variant="primary" onClick={handlePrint} className="me-2">Print</Button>
            <Button variant="secondary" onClick={() => setShowPrintModal(false)}>Close</Button>
          </div>
        </Modal.Header>
        <Modal.Body className="print-body">
          <OrgPrintChrome>
          {printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>UNIT OF MEASUREMENT</h2>
                <p className="text-muted"><strong>{printData.name}</strong> ({printData.symbol})</p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Type:</strong> {printData.type}</p>
                  <p>
                    <strong>Conversion Factor to Base:</strong>{' '}
                    {printData.conversionFactorToBase != null
                      ? Number(printData.conversionFactorToBase).toLocaleString('en-US', { maximumFractionDigits: 10 })
                      : '—'}
                  </p>
                  <p><strong>Description:</strong> {printData.description || '-'}</p>
                  <p><strong>Active:</strong> {printData.isActive ? 'Yes' : 'No'}</p>
                  <p><strong>System:</strong> {printData.isSystem ? 'Yes' : 'No'}</p>
                </Col>
              </Row>
            </div>
          )}
                  </OrgPrintChrome>
        </Modal.Body>
      </Modal>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedUOM ? 'Edit Unit of Measurement' : 'Create Unit of Measurement'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            {selectedUOM?.isSystem && (
              <Alert variant="warning">
                This is a system UOM. Some fields may be restricted.
              </Alert>
            )}
            
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="e.g., Kilogram"
                disabled={selectedUOM?.isSystem}
              />
              <Form.Text className="text-muted">
                Full name of the unit (e.g., Kilogram, Meter, Liter)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Symbol *</Form.Label>
              <Form.Control
                type="text"
                value={formData.symbol}
                onChange={(e) => setFormData({ ...formData, symbol: e.target.value })}
                required
                placeholder="e.g., kg"
                disabled={selectedUOM?.isSystem}
                maxLength={10}
              />
              <Form.Text className="text-muted">
                Short symbol for the unit (e.g., kg, m, L)
              </Form.Text>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Type *</Form.Label>
                  <Form.Select
                    value={formData.type}
                    onChange={(e) => {
                      const type = e.target.value;
                      setFormData({
                        ...formData,
                        type,
                        conversionFactorToBase:
                          type === 'TEMPERATURE'
                            ? ''
                            : (formData.conversionFactorToBase || '1')
                      });
                    }}
                    required
                    disabled={selectedUOM?.isSystem}
                  >
                    <option value="WEIGHT">Weight</option>
                    <option value="LENGTH">Length</option>
                    <option value="VOLUME">Volume</option>
                    <option value="COUNT">Count</option>
                    <option value="AREA">Area</option>
                    <option value="TIME">Time</option>
                    <option value="TEMPERATURE">Temperature</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={formData.isActive ? 'true' : 'false'}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'true' })}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>
                Conversion Factor to Base{formData.type === 'TEMPERATURE' ? '' : ' *'}
              </Form.Label>
              <Form.Control
                type="number"
                step="any"
                min="0"
                value={formData.conversionFactorToBase}
                onChange={(e) => setFormData({ ...formData, conversionFactorToBase: e.target.value })}
                required={formData.type !== 'TEMPERATURE'}
                disabled={formData.type === 'TEMPERATURE'}
                placeholder={formData.type === 'TEMPERATURE' ? 'Not applicable' : 'e.g. 1000 for Liter when base is mL'}
              />
              <Form.Text className="text-muted">
                How many base units equal 1 of this UOM (same type). Examples: mL=1, L=1000; g=1, kg=1000.
                GRN/GIN quantities are converted into the material&apos;s stocking UOM using this factor.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description of the unit"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedUOM ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}

export default UOMManagement;

